import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarX, Settings, TrendingDown, TrendingUp } from 'lucide-react';
import { MeasurementGroupDetails } from './types';
import { useState } from 'react';
import MeasurementGroupStatusUpdateForm from './measurement-group-status-update/MeasurementGroupStatusUpdateForm';
import MinMaxCapacityUpdateForm from './measurement-group-status-update/MinMaxCapacityUpdateForm';
import MeasurementGroupApplyRevisionForm from './apply-revision/MeasurementGroupApplyRevisionForm';
import { ChatBubbleIcon, PersonIcon } from '@radix-ui/react-icons';

type MeasurementGroupOverviewProps = {
	measurementGroup: MeasurementGroupDetails;
};

const MeasurementGroupOverview = (props: MeasurementGroupOverviewProps) => {
	const { measurementGroup } = props;
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [isCapacityFormOpen, setIsCapacityFormOpen] = useState(false);

	const [isRevisionFormOpen, setIsRevisionFormOpen] = useState(false);

	const handleToggleApplyRevisionForm = () => {
		setIsRevisionFormOpen(!isRevisionFormOpen);
	};

	const handleToggleStatusForm = () => {
		setIsFormOpen(!isFormOpen);
	};
	const handleToggleCapacityForm = () => {
		setIsCapacityFormOpen(!isCapacityFormOpen);
	};
	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle>Measurement Group Overview</CardTitle>
					<div className="flex flex-col sm:flex-row gap-2">
						<MeasurementGroupApplyRevisionForm
							isOpen={isRevisionFormOpen}
							handleToggleForm={handleToggleApplyRevisionForm}
							name={measurementGroup.name}
							id={measurementGroup.id}
						/>

						<MeasurementGroupStatusUpdateForm
							isOpen={isFormOpen}
							handleToggleForm={handleToggleStatusForm}
							name={measurementGroup.name}
							id={measurementGroup.id}
							status={measurementGroup.status}
						/>

						<MinMaxCapacityUpdateForm
							isOpen={isCapacityFormOpen}
							handleToggleForm={handleToggleCapacityForm}
						/>
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
							<Settings className="h-5 w-5 text-green-600" />
						</div>
						<div>
							<p className="text-sm font-medium text-muted-foreground">Current Status</p>
							<Badge variant="default">{measurementGroup.status}</Badge>
						</div>
					</div>

					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
							<TrendingDown className="h-5 w-5 text-red-600" />
						</div>
						<div>
							<p className="text-sm font-medium text-muted-foreground">Min. Capacity</p>
							<p className="font-bold">{measurementGroup.graphCapacity.min}</p>
						</div>
					</div>

					<div className="flex items-center space-x-3">
						<div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
							<TrendingUp className="h-5 w-5 text-purple-600" />
						</div>
						<div>
							<p className="text-xs font-medium text-muted-foreground">Max. Capacity</p>
							<p className="font-bold">{measurementGroup.graphCapacity.max}</p>
						</div>
					</div>

					{measurementGroup.latestStatusLog?.changedBy && (
						<div className="flex items-center space-x-3">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<PersonIcon className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Status changed by</p>
								<p className="font-bold">{measurementGroup.latestStatusLog.changedBy}</p>
							</div>
						</div>
					)}

					{measurementGroup.latestStatusLog?.startDate && (
						<div className="flex items-center space-x-3">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<CalendarX className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Status start</p>
								<p className="font-bold">{new Date(measurementGroup.latestStatusLog.startDate).toLocaleString('en-US')}</p>
							</div>
						</div>
					)}

					{measurementGroup.latestStatusLog?.endDate && (
						<div className="flex items-center space-x-3">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<CalendarX className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Status end</p>
								<p className="font-bold">{new Date(measurementGroup.latestStatusLog.endDate).toLocaleString('en-US')}</p>
							</div>
						</div>
					)}

					{measurementGroup.latestStatusLog?.returnDate && (
						<div className="flex items-center space-x-3">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<CalendarX className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Status return</p>
								<p className="font-bold">{new Date(measurementGroup.latestStatusLog.returnDate).toLocaleString('en-US')}</p>
							</div>
						</div>
					)}

					{measurementGroup.latestStatusLog?.internalMessage && (
						<div className="flex items-center space-x-3 col-span-full">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<ChatBubbleIcon className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Internal message</p>
								<p className="font-bold">{measurementGroup.latestStatusLog.internalMessage}</p>
							</div>
						</div>
					)}

					{measurementGroup.latestStatusLog?.customerMessage && (
						<div className="flex items-center space-x-3 col-span-full">
							<div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
								<ChatBubbleIcon className="h-5 w-5 text-gray-600" />
							</div>
							<div>
								<p className="text-xs font-medium text-muted-foreground">Customer message</p>
								<p className="font-bold">{measurementGroup.latestStatusLog.customerMessage}</p>
							</div>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
};

export default MeasurementGroupOverview;
